import { useEffect, useMemo, useState } from 'react';
import { IconButton } from '@teo/components';
import { RotateLeft, RotateRight, Expand, Cross } from '@teo/components/icons';

export const MagnifyImage = (props) => {
  const {
    // component-only props (do NOT forward to <img>)
    showIcon,
    setHoverImage,
    onload, // custom handler name you used; pass to <img> as onLoad
    className = '',
    src,

    // anything else might be intended for <img>, but we'll whitelist safe ones
    ...rest
  } = props;

  // whitelist IMG-safe attrs to avoid React DOM warnings
  const imgProps = useMemo(() => {
    const allow = [
      'alt',
      'loading',
      'decoding',
      'referrerPolicy',
      'sizes',
      'srcSet',
      'width',
      'height',
      'crossOrigin',
      'fetchpriority',
      'draggable',
      'style',
    ];
    const out = {};
    for (const k of allow) if (k in rest) out[k] = rest[k];
    return out;
  }, [rest]);

  const [magnify, setMagnify] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [rotate, setRotate] = useState(0);

  // resolve src (string vs Blob/File)
  const [objectUrl, setObjectUrl] = useState(null);
  const resolvedSrc = useMemo(() => {
    if (typeof src === 'string' || !src) return src;
    // Blob/File
    const url = URL.createObjectURL(src);
    setObjectUrl(url);
    return url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    if (!magnify) setFullscreen(false);
  }, [magnify]);

  // cleanup object URL
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const normalized = ((rotate % 360) + 360) % 360;
  const isVertical = normalized === 90 || normalized === 270;

  return (
    <>
      <div
        className="relative z-[2] overflow-hidden rounded"
        onClick={(ev) => {
          if (magnify) {
            ev.preventDefault();
            setMagnify(false);
          } else if (!showIcon) {
            setMagnify(true);
          }
        }}
        onMouseEnter={() => {
          setHoverImage?.(true);
          setIsMouseOver(true);
        }}
        onMouseLeave={() => {
          setHoverImage?.(false);
          setIsMouseOver(false);
        }}
      >
        <img
          src={resolvedSrc}
          onLoad={onload}
          className={`nowrap ${className}`}
          {...imgProps}
        />

        {showIcon && (
          <button
            type="button"
            className={`absolute right-0 top-0 flex h-full w-full cursor-pointer bg-black/10 transition-opacity ${
              isMouseOver ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              setMagnify(true);
            }}
            aria-label="Enlarge image"
          >
            <Expand className="m-auto w-4 text-grey-05" />
          </button>
        )}
      </div>

      {magnify && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#000000cc]"
          onClick={() => setMagnify(false)}
        >
          {/* Image container */}
          <div
            className={`pointer-events-none flex items-center justify-center ${
              fullscreen ? 'h-screen w-screen' : 'h-full w-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={resolvedSrc}
              className={`pointer-events-auto transition-transform duration-300 ${
                fullscreen ? 'h-full w-full object-contain' : 'h-auto w-auto'
              }`}
              style={{ transform: `rotate(${rotate}deg)` }}
              alt={imgProps.alt}
            />
          </div>

          {/* Floating controls */}
          <div
            className="fixed top-4 right-4 z-[9999] flex gap-2 rounded bg-[#00000088] p-2"
            onClick={(ev) => ev.stopPropagation()}
          >
            <IconButton
              Icon={Expand}
              className="pointer-events-auto fill-white"
              variant="ghost"
              size="md"
              onClick={(ev) => {
                ev.preventDefault();
                setFullscreen((v) => !v);
              }}
              title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            />
            <IconButton
              Icon={RotateLeft}
              className="pointer-events-auto fill-white"
              variant="ghost"
              size="md"
              onClick={(ev) => {
                ev.preventDefault();
                setRotate((r) => r - 90);
              }}
              title="Rotate left"
            />
            <IconButton
              Icon={RotateRight}
              className="pointer-events-auto fill-white"
              variant="ghost"
              size="md"
              onClick={(ev) => {
                ev.preventDefault();
                setRotate((r) => r + 90);
              }}
              title="Rotate right"
            />
            <IconButton
              Icon={Cross}
              className="pointer-events-auto text-white"
              variant="ghost"
              size="md"
              onClick={(ev) => {
                ev.preventDefault();
                setFullscreen(false);
                setMagnify(false);
              }}
              title="Close"
            />
          </div>
        </div>
      )}
    </>
  );
};
